import { clsx } from 'clsx';
import { useId } from 'react';

import styles from './Periods.module.css';

interface Period {
  id: string;
  number: number;
  semesterNumber: number;
  periodInSemester: number;
}

interface TooltipTranslations {
  period: string;
  periods: string;
  and: string;
}

export interface PeriodsProps {
  /** Indicates which periods are selected. */
  periods: Period[] | number[];
  /** Indicates the maximum number of periods to show in the selector. */
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  maxPeriods?: 3 | 6 | 7;
  /** A object of translations for the tooltip. */
  tooltipTranslations?: TooltipTranslations;
}

const MAX_PERIODS = 6;

/** Represents a component for a period indicator. */
export const Periods = ({ periods, tooltipTranslations, maxPeriods = MAX_PERIODS }: PeriodsProps) => {
  const maxPeriodsList = Array.from({ length: maxPeriods }, (_, index) => index + 1);
  const idPrefix = useId();

  const isArrayOfNumbers = periods.some((value) => {
    return typeof value === 'number';
  });

  const getTitles = (tooltipTranslations: TooltipTranslations): string => {
    const semesterPeriods: Record<number, number[]> = {};

    // Group periods by semester
    isArrayOfNumbers
      ? periods.forEach((period) => {
          if (typeof period === 'number') {
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            const groupKey = Math.ceil(period / 3); // Determine the semester (1, 2, or 3)
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            const mappedValue = ((period - 1) % 3) + 1; // Map period to semester

            if (!semesterPeriods[groupKey]) {
              semesterPeriods[groupKey] = [];
            }
            semesterPeriods[groupKey].push(mappedValue);
          }
        })
      : periods.forEach((period) => {
          const { semesterNumber, periodInSemester } = period as Period;

          if (!semesterPeriods[semesterNumber]) {
            semesterPeriods[semesterNumber] = [];
          }
          semesterPeriods[semesterNumber].push(periodInSemester);
        });

    // Build the title string
    const title = Object.keys(semesterPeriods).reduce((acc, semesterKey, index) => {
      const semesterNumber = Number(semesterKey);
      const periodsInSemester = semesterPeriods[semesterNumber];
      let periodTitle = '';

      // Determine if we need singular or plural form for "period"
      const blokLabel = periodsInSemester.length > 1 ? tooltipTranslations.periods : tooltipTranslations.period;

      // Build period sequence for the current semester
      if (periodsInSemester.length > 1) {
        const lastPeriod = periodsInSemester.pop();
        periodTitle = `Semester ${semesterNumber} ${blokLabel} ${periodsInSemester.join(', ')} ${tooltipTranslations.and} ${lastPeriod}`;
      } else {
        periodTitle = `Semester ${semesterNumber} ${blokLabel} ${periodsInSemester[0]}`;
      }

      // Add proper spacing and periods
      return `${acc}${index > 0 ? '. ' : ''}${periodTitle}`;
    }, '');

    return title.trim() + '.';
  };

  const isPeriodActive = (periodIndex: number): boolean => {
    if (isArrayOfNumbers) {
      return (periods as number[]).includes(periodIndex);
    } else {
      return periods.find((period) => (period as Period).number === periodIndex) ? true : false;
    }
  };

  return (
    <div className={styles['periods__container']} title={tooltipTranslations ? getTitles(tooltipTranslations) : ''}>
      {maxPeriodsList.map((periodIndex) => {
        return (
          <span
            className={clsx(
              styles['periods__item'],
              isPeriodActive(periodIndex) ? styles['periods__item--active'] : ''
            )}
            data-period-item
            key={`${idPrefix}-${periodIndex}`}
          />
        );
      })}
    </div>
  );
};
